import type { ConversationLanguage } from "./conversation-adapter";
import type { CarId } from "./protocol";

type LocalizedText = Record<ConversationLanguage, string>;

export const DRIVER_DISPLAY_NAMES: Record<CarId, LocalizedText> = {
  car1: { "ja-JP": "岩佐 歩夢", "en-US": "Ayumu Iwasa", "es-ES": "Ayumu Iwasa", "fr-FR": "Ayumu Iwasa", "zh-CN": "岩佐步梦" },
  car6: { "ja-JP": "太田 格之進", "en-US": "Kakunoshin Ohta", "es-ES": "Kakunoshin Ohta", "fr-FR": "Kakunoshin Ohta", "zh-CN": "太田格之进" },
  car14: { "ja-JP": "福住 仁嶺", "en-US": "Nirei Fukuzumi", "es-ES": "Nirei Fukuzumi", "fr-FR": "Nirei Fukuzumi", "zh-CN": "福住仁岭" },
};

interface LocalizedStory {
  title: LocalizedText;
  body: LocalizedText;
}

const story = (
  titleJa: string, titleEn: string, titleEs: string, titleFr: string, titleZh: string,
  bodyJa: string, bodyEn: string, bodyEs: string, bodyFr: string, bodyZh: string,
): LocalizedStory => ({
  title: { "ja-JP": titleJa, "en-US": titleEn, "es-ES": titleEs, "fr-FR": titleFr, "zh-CN": titleZh },
  body: { "ja-JP": bodyJa, "en-US": bodyEn, "es-ES": bodyEs, "fr-FR": bodyFr, "zh-CN": bodyZh },
});

export const STORY_DISPLAY_TEXT: Record<string, LocalizedStory> = {
  "car1-formation-start": story(
    "フォーメーションラップ", "Formation lap", "Vuelta de formación", "Tour de formation", "暖胎圈",
    "car1のテレメトリはフォーメーションラップの開始を含む。",
    "Car 1 telemetry includes the start of the formation lap.",
    "La telemetría del coche 1 incluye el inicio de la vuelta de formación.",
    "La télémétrie de la voiture 1 comprend le départ du tour de formation.",
    "1号车的遥测数据包含暖胎圈开始时刻。",
  ),
  "car6-formation-start": story(
    "フォーメーションラップ", "Formation lap", "Vuelta de formación", "Tour de formation", "暖胎圈",
    "car6のテレメトリはフォーメーションラップの開始を含む。",
    "Car 6 telemetry includes the start of the formation lap.",
    "La telemetría del coche 6 incluye el inicio de la vuelta de formación.",
    "La télémétrie de la voiture 6 comprend le départ du tour de formation.",
    "6号车的遥测数据包含暖胎圈开始时刻。",
  ),
  "car14-formation-start": story(
    "フォーメーションラップ", "Formation lap", "Vuelta de formación", "Tour de formation", "暖胎圈",
    "car14のテレメトリはフォーメーションラップの開始を含む。",
    "Car 14 telemetry includes the start of the formation lap.",
    "La telemetría del coche 14 incluye el inicio de la vuelta de formación.",
    "La télémétrie de la voiture 14 comprend le départ du tour de formation.",
    "14号车的遥测数据包含暖胎圈开始时刻。",
  ),
  "race-safety-car": story(
    "セーフティカー導入から再開", "Safety car to restart", "Del coche de seguridad al reinicio", "De la voiture de sécurité à la relance", "安全车与比赛重启",
    "序盤にセーフティカーが導入され、4周終了時に再開した。",
    "The safety car was deployed early, and racing resumed after lap 4.",
    "El coche de seguridad salió al principio y la carrera se reanudó tras la vuelta 4.",
    "La voiture de sécurité est intervenue en début de course, puis la course a repris après le 4e tour.",
    "比赛初段出动安全车，并在第4圈结束后重启。",
  ),
  "car1-first-stint-catch-up": story(
    "第1スティントで首位との差を縮める", "Closing the lead gap in stint one", "Recortando la diferencia en el primer stint", "Retour sur la tête au premier relais", "第一赛段缩小与领先者的差距",
    "岩佐は第1スティントでクリーンエアを走り、福住との差を縮めたと説明した。",
    "Iwasa said he ran in clean air during the first stint and closed the gap to Fukuzumi.",
    "Iwasa explicó que rodó con aire limpio en el primer stint y redujo la diferencia con Fukuzumi.",
    "Iwasa a expliqué avoir roulé en air libre lors du premier relais et réduit l'écart sur Fukuzumi.",
    "岩佐表示，他在第一赛段利用干净气流缩小了与福住的差距。",
  ),
  "car6-first-stint-waiting": story(
    "好ペースを保ち、前の2台を追う", "Holding pace behind the leading pair", "Manteniendo el ritmo tras los dos líderes", "Un bon rythme derrière les deux leaders", "保持节奏追赶前方两车",
    "太田は第1スティントを40秒台後半から41秒台前半で走り、先行2台の後ろで機会を待った。",
    "Ohta ran the first stint in the high 1:40s to low 1:41s and waited behind the leading pair.",
    "Ohta rodó el primer stint entre 1:40 altos y 1:41 bajos, esperando detrás de los dos líderes.",
    "Ohta a couvert le premier relais entre 1 min 40 s élevées et 1 min 41 s basses, en attendant derrière les deux leaders.",
    "太田在第一赛段保持1分40秒后段至1分41秒前段的圈速，等待超越前方两车的机会。",
  ),
  "car14-late-stint-lap-observation": story(
    "ピット後の周回所要時間", "Post-stop lap times", "Tiempos de vuelta tras la parada", "Temps au tour après l'arrêt", "进站后的圈速",
    "福住のピット後の通常走行は24周目から31周目まで続き、周回所要時間は100.389秒から101.590秒の範囲で推移した。",
    "Fukuzumi's normal running after the stop lasted from laps 24 to 31, with lap times from 100.389 to 101.590 seconds.",
    "El ritmo normal de Fukuzumi tras la parada duró de la vuelta 24 a la 31, con tiempos de 100,389 a 101,590 segundos.",
    "Le roulage normal de Fukuzumi après l'arrêt s'est poursuivi des tours 24 à 31, entre 100,389 et 101,590 secondes.",
    "福住进站后的正常行驶持续至第24至31圈，圈速在100.389至101.590秒之间。",
  ),
  "car14-late-pace-and-rear-tyres": story(
    "本人コメント: 後半はペースと後輪タイヤが厳しい状況", "Driver comment: difficult late pace and rear tyres", "Comentario: ritmo y neumáticos traseros difíciles al final", "Commentaire : rythme et pneus arrière difficiles en fin de course", "车手评论：后半程节奏和后轮状况艰难",
    "福住は、後半のペースと後輪タイヤの状態が厳しく、追われ続けたと説明した。",
    "Fukuzumi said his late-race pace and rear-tyre condition were difficult while he remained under pressure.",
    "Fukuzumi explicó que el ritmo final y el estado de los neumáticos traseros eran difíciles mientras seguía bajo presión.",
    "Fukuzumi a expliqué que son rythme et l'état de ses pneus arrière étaient difficiles en fin de course, sous une pression constante.",
    "福住表示，后半程的节奏和后轮状态都很艰难，并持续承受追击压力。",
  ),
  "car14-overtaken-lap26": story(
    "岩佐が130R進入で首位へ", "Iwasa takes the lead into 130R", "Iwasa toma el liderato en la entrada de 130R", "Iwasa prend la tête à l'entrée du 130R", "岩佐在130R入口取得领先",
    "26周目に岩佐が130R進入で福住を抜いた。", "Iwasa passed Fukuzumi into 130R on lap 26.",
    "Iwasa adelantó a Fukuzumi en la entrada de 130R en la vuelta 26.", "Iwasa a dépassé Fukuzumi à l'entrée du 130R au 26e tour.", "第26圈，岩佐在130R入口超越福住。",
  ),
  "car14-retake-lap27": story(
    "福住が1コーナーで首位を奪い返す", "Fukuzumi retakes the lead at Turn 1", "Fukuzumi recupera el liderato en la curva 1", "Fukuzumi reprend la tête au premier virage", "福住在1号弯夺回领先",
    "27周目に福住が直線速度を生かして1コーナーで首位を奪い返した。", "Fukuzumi used his straight-line speed to retake the lead at Turn 1 on lap 27.",
    "Fukuzumi aprovechó su velocidad en recta para recuperar el liderato en la curva 1 en la vuelta 27.", "Fukuzumi a profité de sa vitesse en ligne droite pour reprendre la tête au premier virage au 27e tour.", "第27圈，福住利用直线速度在1号弯夺回领先。",
  ),
  "car6-final-attack": story(
    "太田が終盤に2位を狙う", "Ohta attacks for second late on", "Ohta ataca por el segundo puesto al final", "Ohta attaque pour la deuxième place en fin de course", "太田在比赛末段争夺第二",
    "太田は30周目と最終周に岩佐を攻めた。", "Ohta attacked Iwasa on lap 30 and the final lap.",
    "Ohta atacó a Iwasa en la vuelta 30 y en la última vuelta.", "Ohta a attaqué Iwasa au 30e tour puis dans le dernier tour.", "太田在第30圈和最后一圈向岩佐发起进攻。",
  ),
  "race-result-top-three": story(
    "福住が優勝、岩佐2位、太田3位", "Fukuzumi wins, Iwasa second, Ohta third", "Fukuzumi gana, Iwasa segundo y Ohta tercero", "Fukuzumi gagne, Iwasa deuxième, Ohta troisième", "福住夺冠，岩佐第二，太田第三",
    "福住が優勝し、岩佐が0.240秒差の2位、太田が0.620秒差の3位でゴールした。",
    "Fukuzumi won; Iwasa finished second, 0.240 seconds behind, and Ohta third, 0.620 seconds behind.",
    "Fukuzumi ganó; Iwasa terminó segundo a 0,240 segundos y Ohta tercero a 0,620 segundos.",
    "Fukuzumi s'est imposé ; Iwasa a terminé deuxième à 0,240 seconde et Ohta troisième à 0,620 seconde.",
    "福住夺冠；岩佐落后0.240秒获得第二，太田落后0.620秒获得第三。",
  ),
};

export function localizedDriverName(carId: CarId, language: ConversationLanguage): string {
  return DRIVER_DISPLAY_NAMES[carId][language];
}

export function localizedStoryText(
  evidenceId: string,
  language: ConversationLanguage,
  fallbackTitle: string,
  fallbackBody: string,
): { title: string; body: string } {
  const item = STORY_DISPLAY_TEXT[evidenceId];
  return item ? { title: item.title[language], body: item.body[language] } : {
    title: fallbackTitle,
    body: fallbackBody,
  };
}
